import React, { useRef, useState, Fragment } from "react"
import { useTranslation } from "react-i18next"
import { useTeams } from "../../../../hooks/useTeams"
import { Image } from "react-bootstrap"
import type { FormikProps } from "formik"
import { HiEyeOff, HiEye, HiTrash, HiOutlinePhotograph } from "react-icons/hi"
import { Switch } from "@headlessui/react"
import { capitalize, twClassNames } from "../../../../helpers"
import DragZone from "../../../../components/dragZone"
import Tooltip from "../../../../components/tooltip"
import { isLevi } from "../../../../helpers/isLevi"
import type { DestinationFormValues } from "../../../../constants/types"

type SettingsFormProps = {
  formik: FormikProps<DestinationFormValues>
  isLoading: boolean
}

const SettingsForm: React.FC<SettingsFormProps> = ({ formik, isLoading }) => {
  const { t } = useTranslation()
  const imageRef = useRef<HTMLInputElement>(null)
  const [showPincode, setShowPincode] = useState(false)
  const { data: teams = [] } = useTeams()
  const userRole = teams?.find(team => team.id === formik.values?.ownerTeamId)?.userRole

  const onReset = () => {
    formik.handleReset()
    if (imageRef.current) {
      imageRef.current.value = ""
    }
  }

  const errorClass = (field: keyof DestinationFormValues) =>
    formik.touched[field] && formik.errors[field] && "tw-is-invalid"

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <div className="tw-w-full md:tw-pr-6 max-md:tw-pb-6">
        <fieldset className="tw-grid tw-grid-cols md:tw-grid-cols-6 tw-gap-x-2 tw-gap-y-3 md:tw-gap-y-4">
          {/* Active */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="isPublic" className="tw-form-label">
              {t("settings.form.isPublic")}
            </label>
            <div className="tw-flex tw-space-x-2">
              <Switch
                disabled={userRole === "levi_maintainer"}
                name="isPublic"
                checked={formik.values.isPublic}
                onChange={(value: boolean) => formik.setFieldValue("isPublic", value)}
                as={Fragment}>
                {({ checked }: { checked: boolean }) => (
                  <span className="tw-flex">
                    <button
                      id="isPublic"
                      disabled={userRole === "levi_maintainer"}
                      className={twClassNames(
                        checked ? "tw-bg-primary" : "tw-bg-dark-25",
                        "tw-relative tw-inline-flex tw-h-6 tw-w-11 tw-items-center tw-rounded-full",
                        `disabled:tw-opacity-25 focus-within:tw-outline-none focus-within:tw-ring-2
                        focus-within:tw-ring-primary-50`
                      )}>
                      <span
                        className={twClassNames(
                          checked ? "tw-translate-x-6" : "tw-translate-x-1",
                          "tw-inline-block tw-h-4 tw-w-4 tw-transform tw-rounded-full tw-bg-white tw-transition"
                        )}
                      />
                    </button>
                    <span className="tw-ml-3 tw-text-sm">
                      {checked ? t("settings.form.active") : t("settings.form.unactive")}
                    </span>
                  </span>
                )}
              </Switch>
            </div>
          </div>
          {/* Access code */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="accessCode" className="tw-form-label">
              {t("settings.form.accessCode")}
              <Tooltip message="settings.tooltips.accessCode" />
            </label>
            <input
              disabled={!isLevi(formik.values.ownerTeamId) || userRole === "levi_maintainer"}
              type="text"
              id="accessCode"
              name="accessCode"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.accessCode}
              className={twClassNames(errorClass("accessCode"), "tw-form-control")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.accessCode}</div>
          </div>
          {/* Pincode */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="pincode" className="tw-form-label">
              {t("settings.form.pincode")}
              <span className="tw-form-label-optional">({t("common.validation.optional")})</span>
              <Tooltip message="settings.tooltips.pincode" />
            </label>
            <span className="tw-relative">
              <input
                disabled={userRole === "levi_maintainer"}
                type={showPincode ? "text" : "password"}
                id="pincode"
                name="pincode"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.pincode}
                maxLength={4}
                className={twClassNames(errorClass("pincode"), "tw-form-control !tw-pr-9")}
                spellCheck="false"
              />
              <button
                type="button"
                onClick={() => setShowPincode(!showPincode)}
                className={twClassNames("tw-absolute tw-right-2.5 tw-top-0 tw-my-1.5 tw-bg-white")}>
                {showPincode ? <HiEyeOff /> : <HiEye />}
              </button>
              <div className="tw-invalid-tooltip">{formik.errors.pincode}</div>
            </span>
          </div>
          {/* Basic informations */}
          <div className="tw-relative tw-col-span-6 tw-border-b tw-mt-4">
            <h5>{t("settings.form.subtitle.name")}</h5>
            <p className="tw-text-sm tw-text-dark-75">{t("settings.form.subtitle.info")}</p>
          </div>
          {/* Name (fi) */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="nameFi" className="tw-form-label">
              {t("settings.form.nameFi")}
            </label>
            <input
              type="text"
              id="nameFi"
              name="nameFi"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.nameFi}
              className={twClassNames(errorClass("nameFi"), "tw-form-control")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.nameFi}</div>
          </div>
          {/* Name (eng) */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="nameEn" className="tw-form-label">
              {t("settings.form.nameEn")}
            </label>
            <input
              type="text"
              id="nameEn"
              name="nameEn"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.nameEn}
              className={twClassNames(errorClass("nameEn"), "tw-form-control")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.nameEn}</div>
          </div>
          {/* Street Name */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="streetName" className="tw-form-label">
              {t("settings.form.streetName")}
            </label>
            <input
              type="text"
              id="streetName"
              name="streetName"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.streetName}
              className={twClassNames(errorClass("streetName"), "tw-form-control")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.streetName}</div>
          </div>
          {/* City */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="city" className="tw-form-label">
              {t("settings.form.city")}
            </label>
            <input
              type="text"
              id="city"
              name="city"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={capitalize(formik.values.city)}
              className={twClassNames(errorClass("city"), "tw-form-control")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.city}</div>
          </div>
          {/* Phone */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="phone" className="tw-form-label">
              {t("settings.form.phone")}
              <span className="tw-form-label-optional">({t("common.validation.optional")})</span>
              <Tooltip message="settings.tooltips.phone" />
            </label>
            <input
              disabled={userRole === "levi_maintainer"}
              type="phone"
              id="phone"
              name="phone"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.phone}
              className={twClassNames(errorClass("phone"), "tw-form-control")}
            />
            <div className="tw-invalid-tooltip">{formik.errors.phone}</div>
          </div>
          {/* Description FI */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="descriptionFi" className="tw-form-label">
              {t("settings.form.descriptionFi")}
              <Tooltip message="settings.tooltips.descriptionFi" />
            </label>
            <textarea
              disabled={userRole === "levi_maintainer"}
              id="descriptionFi"
              name="descriptionFi"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.descriptionFi}
              className={twClassNames(errorClass("descriptionFi"), "tw-form-control tw-min-h-[150px]")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.descriptionFi}</div>
          </div>
          {/* Description EN */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="descriptionEn" className="tw-form-label">
              {t("settings.form.descriptionEn")}
            </label>
            <textarea
              disabled={userRole === "levi_maintainer"}
              id="descriptionEn"
              name="descriptionEn"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.descriptionEn}
              className={twClassNames(errorClass("descriptionEn"), "tw-form-control tw-min-h-[150px]")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.descriptionEn}</div>
          </div>
          {/*  Call button */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3 lg:tw-col-span-2">
            <label htmlFor="displayCall" className="tw-form-label">
              {t("settings.form.displayCall")}
            </label>
            <div className="tw-flex tw-space-x-2">
              <Switch
                disabled={userRole === "levi_maintainer"}
                name="displayCall"
                checked={formik.values.displayCall}
                onChange={(value: boolean) => formik.setFieldValue("displayCall", value)}
                as={Fragment}>
                {({ checked }: { checked: boolean }) => (
                  <button
                    id="displayCall"
                    className={twClassNames(
                      checked ? "tw-bg-primary" : "tw-bg-dark-25",
                      "tw-relative tw-inline-flex tw-h-6 tw-w-11 tw-items-center tw-rounded-full",
                      "focus-within:tw-outline-none focus-within:tw-ring-2 focus-within:tw-ring-primary-50"
                    )}>
                    <span
                      className={twClassNames(
                        checked ? "tw-translate-x-6" : "tw-translate-x-1",
                        "tw-inline-block tw-h-4 tw-w-4 tw-transform tw-rounded-full tw-bg-white tw-transition"
                      )}
                    />
                  </button>
                )}
              </Switch>
            </div>
          </div>
          {/* Message button */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3 lg:tw-col-span-2">
            <label htmlFor="displayMessage" className="tw-form-label">
              {t("settings.form.displayMessage")}
            </label>
            <div className="tw-flex tw-space-x-2">
              <Switch
                disabled={userRole === "levi_maintainer"}
                name="displayMessage"
                checked={formik.values.displayMessage}
                onChange={(value: boolean) => formik.setFieldValue("displayMessage", value)}
                as={Fragment}>
                {({ checked }: { checked: boolean }) => (
                  <button
                    id="displayMessage"
                    className={twClassNames(
                      checked ? "tw-bg-primary" : "tw-bg-dark-25",
                      "tw-relative tw-inline-flex tw-h-6 tw-w-11 tw-items-center tw-rounded-full",
                      "focus-within:tw-outline-none focus-within:tw-ring-2 focus-within:tw-ring-primary-50"
                    )}>
                    <span
                      className={twClassNames(
                        checked ? "tw-translate-x-6" : "tw-translate-x-1",
                        "tw-inline-block tw-h-4 tw-w-4 tw-transform tw-rounded-full tw-bg-white tw-transition"
                      )}
                    />
                  </button>
                )}
              </Switch>
            </div>
          </div>
          {/* Image */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="image" className="tw-form-label">
              {t("settings.form.image")}
              <span className="tw-form-label-optional">({t("common.validation.optional")})</span>
            </label>
            {formik.values.image ? (
              <div className="tw-relative tw-w-fit">
                <Image
                  thumbnail
                  onClick={() => imageRef.current?.click()}
                  className={twClassNames("background-thumbnail", userRole === "levi_maintainer" && "tw-opacity-50")}
                  src={
                    typeof formik.values.image === "string"
                      ? formik.values.image
                      : URL.createObjectURL(formik.values.image)
                  }
                />
                <button
                  disabled={userRole === "levi_maintainer"}
                  type="button"
                  onClick={() => formik.setFieldValue("image", null)}
                  className="tw-absolute tw-bottom-0 tw-right-4 tw-bg-red-400 tw-rounded-full disabled:tw-opacity-50">
                  <HiTrash className="tw-text-white tw-h-8 tw-w-8 tw-p-2" />
                </button>
              </div>
            ) : (
              <DragZone
                onDrop={(data: File) => formik.setFieldValue("image", data)}
                onClick={() => imageRef.current?.click()}
                className="tw-flex tw-justify-center tw-items-center tw-rounded-md tw-px-6 tw-py-4 tw-border-2
                  tw-border-dashed tw-cursor-pointer">
                <div className="tw-space-y-1 tw-text-center">
                  <HiOutlinePhotograph className="tw-mx-auto tw-h-12 tw-w-12 tw-text-dark-25 [&>*]:tw-stroke-1" />
                  <div className="tw-flex tw-text-sm tw-text-dark-75">
                    <label
                      htmlFor="image"
                      className="tw-relative tw-rounded-md tw-bg-white tw-font-medium tw-text-primary">
                      <span>{t("common.components.dragZone.upload")}</span>
                    </label>
                    <p className="tw-pl-1">{t("common.components.dragZone.dragAndDrop")}</p>
                  </div>
                  <p className="tw-text-xs tw-text-dark-50">{t("common.components.dragZone.type")}</p>
                </div>
              </DragZone>
            )}
            <input
              type="file"
              name="image"
              accept="image/*"
              className="tw-hidden tw-sr-only"
              ref={imageRef}
              onChange={event => formik.setFieldValue("image", event.currentTarget.files?.[0])}
            />
            <div className="tw-invalid-tooltip">{formik.errors.image}</div>
          </div>
        </fieldset>
      </div>
      {formik.dirty && (
        <div className="tw-fixed-footer tw-has-sidebar tw-bg-white tw-shadow-2xl tw-border-t tw-z-10 tw-py-4">
          <div className="tw-px-6 tw-flex tw-w-full tw-items-center tw-justify-between">
            <span>{t("common.unsavedChanges.content")}</span>
            <div className="tw-flex tw-flex-row tw-space-x-4">
              <button
                type="reset"
                disabled={isLoading}
                onClick={onReset}
                className="tw-btn tw-btn-outline tw-min-w-[180px]">
                {t("common.unsavedChanges.button.cancel")}
              </button>
              <button type="submit" disabled={isLoading} className="tw-btn tw-btn-primary tw-min-w-[180px]">
                {t("common.unsavedChanges.button.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  )
}

export default SettingsForm
